import React, { useState, useEffect } from 'react';
import { X, Gift, Download, Loader2, CheckCircle, BookOpen, ClipboardCheck } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExitIntentModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedGuides, setSelectedGuides] = useState(['erros', 'checklist']);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const { t, currentLanguage } = useLanguage();

  const content = {
    pt: {
      title: 'Espere! Temos um presente para si',
      subtitle: 'Antes de ir, leve estes guias gratuitos',
      description: 'Dois guias exclusivos que vão ajudar a planear o casamento dos vossos sonhos, sem stress!',
      guide1Title: '10 Erros a Evitar',
      guide1Desc: 'Os erros mais comuns no planeamento e como evitá-los',
      guide2Title: 'Checklist do Dia',
      guide2Desc: 'Lista completa para o grande dia, hora a hora',
      namePlaceholder: 'O vosso nome',
      emailPlaceholder: 'email@exemplo.com',
      button: 'Quero os Guias Gratuitos!',
      success: 'Guias Enviados!',
      checkEmail: 'Verifique o seu email',
      noThanks: 'Não, obrigado'
    },
    en: {
      title: 'Wait! We have a gift for you',
      subtitle: 'Before you go, take these free guides',
      description: 'Two exclusive guides that will help you plan your dream wedding, stress-free!',
      guide1Title: '10 Mistakes to Avoid',
      guide1Desc: 'The most common planning mistakes and how to avoid them',
      guide2Title: 'Day Checklist',
      guide2Desc: 'Complete list for the big day, hour by hour',
      namePlaceholder: 'Your name',
      emailPlaceholder: 'email@example.com',
      button: 'I Want the Free Guides!',
      success: 'Guides Sent!',
      checkEmail: 'Check your email',
      noThanks: 'No, thanks'
    },
    fr: {
      title: 'Attendez! Nous avons un cadeau pour vous',
      subtitle: 'Avant de partir, prenez ces guides gratuits',
      description: 'Deux guides exclusifs qui vous aideront à planifier le mariage de vos rêves, sans stress!',
      guide1Title: '10 Erreurs à Éviter',
      guide1Desc: 'Les erreurs les plus courantes et comment les éviter',
      guide2Title: 'Checklist du Jour',
      guide2Desc: 'Liste complète pour le grand jour, heure par heure',
      namePlaceholder: 'Votre nom',
      emailPlaceholder: 'email@exemple.com',
      button: 'Je Veux les Guides Gratuits!',
      success: 'Guides Envoyés!',
      checkEmail: 'Vérifiez votre email',
      noThanks: 'Non, merci'
    },
    es: {
      title: '¡Espera! Tenemos un regalo para ti',
      subtitle: 'Antes de irte, llévate estas guías gratuitas',
      description: '¡Dos guías exclusivas que te ayudarán a planificar la boda de tus sueños, sin estrés!',
      guide1Title: '10 Errores a Evitar',
      guide1Desc: 'Los errores más comunes y cómo evitarlos',
      guide2Title: 'Checklist del Día',
      guide2Desc: 'Lista completa para el gran día, hora a hora',
      namePlaceholder: 'Vuestro nombre',
      emailPlaceholder: 'email@ejemplo.com',
      button: '¡Quiero las Guías Gratuitas!',
      success: '¡Guías Enviadas!',
      checkEmail: 'Revisa tu email',
      noThanks: 'No, gracias'
    }
  };

  const c = content[currentLanguage] || content.pt;

  useEffect(() => {
    // Check if already shown in this session
    const shown = sessionStorage.getItem('exitIntentShown');
    if (shown) {
      setHasShown(true);
      return;
    }

    const handleMouseLeave = (e) => {
      // Only trigger when mouse leaves at the top of the page
      if (e.clientY <= 0 && !hasShown) {
        setIsOpen(true);
        setHasShown(true);
        sessionStorage.setItem('exitIntentShown', 'true');
      }
    };

    // Add delay before enabling exit intent
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 10000); // Wait 10 seconds before enabling

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [hasShown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/lead-magnet/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          guides: selectedGuides
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao enviar');
      }

      setSent(true);
      
      // Close after 3 seconds
      setTimeout(() => {
        setIsOpen(false);
      }, 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {sent ? (
          <div className="p-10 text-center">
            <div className="w-20 h-20 rounded-full bg-[#B5C4B1]/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-[#B5C4B1]" />
            </div>
            <h3 className="font-['Cormorant_Garamond'] text-3xl text-[#2C2C2C] mb-3">
              {c.success}
            </h3>
            <p className="text-[#6B4E37]">{c.checkEmail}</p>
          </div>
        ) : (
          <>
            {/* Header with gradient */}
            <div className="relative bg-gradient-to-br from-[#D4C4B0] to-[#B5C4B1] p-8 text-center">
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-2 text-white/80 hover:text-white transition-colors"
                aria-label="Fechar"
              >
                <X size={24} />
              </button>
              
              <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Gift size={32} className="text-[#6B4E37]" />
              </div>
              
              <h2 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl text-white mb-2">
                {c.title}
              </h2>
              <p className="text-white/90 text-sm">
                {c.subtitle}
              </p>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-[#6B4E37] text-center mb-6 text-sm">
                {c.description}
              </p>

              {/* Guides preview */}
              <div className="flex gap-4 mb-6">
                <div className="flex-1 flex items-center gap-3 p-3 bg-[#FAF9F7] rounded-lg">
                  <div className="w-10 h-10 rounded-lg bg-[#E8D5D5] flex items-center justify-center flex-shrink-0">
                    <BookOpen size={18} className="text-[#6B4E37]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-[#2C2C2C]">{c.guide1Title}</h4>
                    <p className="text-xs text-[#8B7D6B]">{c.guide1Desc}</p>
                  </div>
                </div>
                <div className="flex-1 flex items-center gap-3 p-3 bg-[#FAF9F7] rounded-lg">
                  <div className="w-10 h-10 rounded-lg bg-[#B5C4B1] flex items-center justify-center flex-shrink-0">
                    <ClipboardCheck size={18} className="text-[#6B4E37]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-[#2C2C2C]">{c.guide2Title}</h4>
                    <p className="text-xs text-[#8B7D6B]">{c.guide2Desc}</p>
                  </div>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={c.namePlaceholder}
                    required
                    className="w-full px-4 py-3 bg-[#FAF9F7] border border-[#E8E4E0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4B5A0] text-sm"
                    data-testid="exit-name"
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={c.emailPlaceholder}
                    required
                    className="w-full px-4 py-3 bg-[#FAF9F7] border border-[#E8E4E0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4B5A0] text-sm"
                    data-testid="exit-email"
                  />
                </div>

                {error && (
                  <p className="text-red-500 text-sm text-center">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#C4B5A0] text-white rounded-lg font-medium hover:bg-[#6B4E37] transition-colors disabled:opacity-50"
                  data-testid="exit-submit"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Download size={18} />
                      {c.button}
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleClose}
                  className="w-full text-sm text-[#8B7D6B] hover:text-[#6B4E37] transition-colors py-2"
                >
                  {c.noThanks}
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ExitIntentModal;
